import type {
  EditorTool,
  BrushSettings,
  TextSettings,
  ShapeSettings,
  FilterSettings,
  ShapeType,
} from '../types';

interface PropertiesPanelProps {
  isOpen: boolean;
  activeTool: EditorTool;
  brushSettings: BrushSettings;
  textSettings: TextSettings;
  shapeSettings: ShapeSettings;
  filterSettings: FilterSettings;
  onUpdateBrush: (settings: Partial<BrushSettings>) => void;
  onUpdateText: (settings: Partial<TextSettings>) => void;
  onUpdateShape: (settings: Partial<ShapeSettings>) => void;
  onUpdateFilter: (settings: Partial<FilterSettings>) => void;
  onToggle: () => void;
}

export function PropertiesPanel({
  isOpen,
  activeTool,
  brushSettings,
  textSettings,
  shapeSettings,
  filterSettings,
  onUpdateBrush,
  onUpdateText,
  onUpdateShape,
  onUpdateFilter,
  onToggle,
}: PropertiesPanelProps) {
  if (!isOpen) {
    return (
      <button
        type="button"
        className="w-8 bg-background border-l border-border flex items-center justify-center text-muted-foreground hover:text-foreground"
        onClick={onToggle}
        title="Show properties"
      >
        <span className="text-xs [writing-mode:vertical-lr]">Properties</span>
      </button>
    );
  }

  return (
    <div className="w-[280px] bg-background border-l border-border overflow-y-auto">
      <div className="flex items-center justify-between p-3 border-b border-border">
        <h3 className="text-sm font-medium">Properties</h3>
        <button
          type="button"
          className="text-xs text-muted-foreground hover:text-foreground"
          onClick={onToggle}
        >
          Hide
        </button>
      </div>

      <div className="p-3 space-y-4">
        {activeTool === 'draw' && <BrushPanel settings={brushSettings} onChange={onUpdateBrush} />}
        {activeTool === 'eraser' && (
          <BrushPanel settings={brushSettings} onChange={onUpdateBrush} eraserMode />
        )}
        {activeTool === 'text' && <TextPanel settings={textSettings} onChange={onUpdateText} />}
        {activeTool === 'shape' && <ShapePanel settings={shapeSettings} onChange={onUpdateShape} />}
        {/* Filter panel is always available */}
        <FilterPanel settings={filterSettings} onChange={onUpdateFilter} />
      </div>
    </div>
  );
}

function BrushPanel({
  settings,
  onChange,
  eraserMode,
}: {
  settings: BrushSettings;
  onChange: (s: Partial<BrushSettings>) => void;
  eraserMode?: boolean;
}) {
  return (
    <div className="space-y-3">
      <h4 className="text-xs font-medium text-muted-foreground uppercase">
        {eraserMode ? 'Eraser' : 'Brush'}
      </h4>
      {!eraserMode && (
        <label className="flex items-center gap-2">
          <span className="text-xs w-14">Color</span>
          <input
            type="color"
            value={settings.color}
            onChange={(e) => onChange({ color: e.target.value })}
            className="w-8 h-8 rounded border border-border cursor-pointer"
          />
        </label>
      )}
      <label className="flex items-center gap-2">
        <span className="text-xs w-14">Size</span>
        <input
          type="range"
          min={1}
          max={100}
          value={settings.width}
          onChange={(e) => onChange({ width: Number(e.target.value) })}
          className="flex-1"
        />
        <span className="text-xs w-8 text-right tabular-nums">{settings.width}</span>
      </label>
      {!eraserMode && (
        <label className="flex items-center gap-2">
          <span className="text-xs w-14">Opacity</span>
          <input
            type="range"
            min={0}
            max={100}
            value={Math.round(settings.opacity * 100)}
            onChange={(e) => onChange({ opacity: Number(e.target.value) / 100 })}
            className="flex-1"
          />
          <span className="text-xs w-8 text-right tabular-nums">
            {Math.round(settings.opacity * 100)}%
          </span>
        </label>
      )}
    </div>
  );
}

function TextPanel({
  settings,
  onChange,
}: {
  settings: TextSettings;
  onChange: (s: Partial<TextSettings>) => void;
}) {
  return (
    <div className="space-y-3">
      <h4 className="text-xs font-medium text-muted-foreground uppercase">Text</h4>
      <label className="flex items-center gap-2">
        <span className="text-xs w-14">Font</span>
        <select
          value={settings.fontFamily}
          onChange={(e) => onChange({ fontFamily: e.target.value })}
          className="flex-1 text-xs bg-background border border-border rounded px-2 py-1"
        >
          <option value="Arial">Arial</option>
          <option value="Helvetica">Helvetica</option>
          <option value="Georgia">Georgia</option>
          <option value="Times New Roman">Times New Roman</option>
          <option value="Courier New">Courier New</option>
          <option value="Verdana">Verdana</option>
        </select>
      </label>
      <label className="flex items-center gap-2">
        <span className="text-xs w-14">Size</span>
        <input
          type="number"
          min={8}
          max={200}
          value={settings.fontSize}
          onChange={(e) => onChange({ fontSize: Number(e.target.value) })}
          className="flex-1 text-xs bg-background border border-border rounded px-2 py-1"
        />
      </label>
      <label className="flex items-center gap-2">
        <span className="text-xs w-14">Color</span>
        <input
          type="color"
          value={settings.fill}
          onChange={(e) => onChange({ fill: e.target.value })}
          className="w-8 h-8 rounded border border-border cursor-pointer"
        />
      </label>
      <div className="flex gap-1">
        <button
          type="button"
          className={`px-2 py-1 text-xs rounded ${settings.fontWeight === 'bold' ? 'bg-primary text-primary-foreground' : 'bg-accent'}`}
          onClick={() =>
            onChange({ fontWeight: settings.fontWeight === 'bold' ? 'normal' : 'bold' })
          }
        >
          B
        </button>
        <button
          type="button"
          className={`px-2 py-1 text-xs rounded italic ${settings.fontStyle === 'italic' ? 'bg-primary text-primary-foreground' : 'bg-accent'}`}
          onClick={() =>
            onChange({ fontStyle: settings.fontStyle === 'italic' ? 'normal' : 'italic' })
          }
        >
          I
        </button>
        <button
          type="button"
          className={`px-2 py-1 text-xs rounded underline ${settings.underline ? 'bg-primary text-primary-foreground' : 'bg-accent'}`}
          onClick={() => onChange({ underline: !settings.underline })}
        >
          U
        </button>
      </div>
    </div>
  );
}

function ShapePanel({
  settings,
  onChange,
}: {
  settings: ShapeSettings;
  onChange: (s: Partial<ShapeSettings>) => void;
}) {
  const shapes: { type: ShapeType; label: string }[] = [
    { type: 'rect', label: 'Rectangle' },
    { type: 'circle', label: 'Circle' },
    { type: 'triangle', label: 'Triangle' },
    { type: 'line', label: 'Line' },
  ];

  return (
    <div className="space-y-3">
      <h4 className="text-xs font-medium text-muted-foreground uppercase">Shape</h4>
      <div className="flex gap-1 flex-wrap">
        {shapes.map((s) => (
          <button
            key={s.type}
            type="button"
            className={`px-2 py-1 text-xs rounded ${settings.type === s.type ? 'bg-primary text-primary-foreground' : 'bg-accent'}`}
            onClick={() => onChange({ type: s.type })}
          >
            {s.label}
          </button>
        ))}
      </div>
      <label className="flex items-center gap-2">
        <span className="text-xs w-14">Fill</span>
        <input
          type="color"
          value={settings.fill === 'transparent' ? '#ffffff' : settings.fill}
          onChange={(e) => onChange({ fill: e.target.value })}
          className="w-8 h-8 rounded border border-border cursor-pointer"
        />
        <button
          type="button"
          className={`text-xs px-2 py-0.5 rounded ${settings.fill === 'transparent' ? 'bg-primary text-primary-foreground' : 'bg-accent'}`}
          onClick={() =>
            onChange({ fill: settings.fill === 'transparent' ? '#ffffff' : 'transparent' })
          }
        >
          {settings.fill === 'transparent' ? 'No fill' : 'Clear'}
        </button>
      </label>
      <label className="flex items-center gap-2">
        <span className="text-xs w-14">Stroke</span>
        <input
          type="color"
          value={settings.stroke}
          onChange={(e) => onChange({ stroke: e.target.value })}
          className="w-8 h-8 rounded border border-border cursor-pointer"
        />
      </label>
      <label className="flex items-center gap-2">
        <span className="text-xs w-14">Width</span>
        <input
          type="range"
          min={0}
          max={20}
          value={settings.strokeWidth}
          onChange={(e) => onChange({ strokeWidth: Number(e.target.value) })}
          className="flex-1"
        />
        <span className="text-xs w-8 text-right tabular-nums">{settings.strokeWidth}</span>
      </label>
    </div>
  );
}

function FilterPanel({
  settings,
  onChange,
}: {
  settings: FilterSettings;
  onChange: (s: Partial<FilterSettings>) => void;
}) {
  const filters: { key: keyof FilterSettings; label: string }[] = [
    { key: 'brightness', label: 'Brightness' },
    { key: 'contrast', label: 'Contrast' },
    { key: 'saturation', label: 'Saturation' },
  ];

  return (
    <div className="space-y-3">
      <h4 className="text-xs font-medium text-muted-foreground uppercase">Filters</h4>
      {filters.map((f) => (
        <label key={f.key} className="flex items-center gap-2">
          <span className="text-xs w-16">{f.label}</span>
          <input
            type="range"
            min={-100}
            max={100}
            value={settings[f.key]}
            onChange={(e) => onChange({ [f.key]: Number(e.target.value) })}
            className="flex-1"
          />
          <span className="text-xs w-8 text-right tabular-nums">{settings[f.key]}</span>
        </label>
      ))}
      <button
        type="button"
        className="text-xs text-muted-foreground hover:text-foreground"
        onClick={() => onChange({ brightness: 0, contrast: 0, saturation: 0 })}
      >
        Reset filters
      </button>
    </div>
  );
}
